/**
 * Walkthrough scenario library (ProjectTasks #109).
 *
 * The first scenario mirrors a realistic A&D PMO ramp-up: a program office
 * standing up a portfolio, staffing it, baselining a project, and running
 * the EVM / change-control loop. Each step navigates to the real page and
 * tells the user what to do there.
 *
 * Routes are verified against App.tsx. Selectors are intentionally sparse:
 * steps that don't name a target render a centered card. Add `data-tour`
 * anchors to page elements to upgrade a step to a spotlight (the overlay
 * falls back to centered automatically when a selector misses).
 */

import type { WalkthroughScenario } from './types'
import { anyCellFilled, personaChanged, textVisible } from './stepDetection'

export const AD_PMO_RAMPUP: WalkthroughScenario = {
  id: 'ad-pmo-rampup',
  name: 'Aerospace & Defense PMO Ramp-Up',
  description: 'Build a realistic defense-program portfolio from scratch: org, staffing, baseline, and the EVM change-control loop.',
  estMinutes: 25,
  steps: [
    {
      id: 'org-structure',
      title: 'Create your org structure',
      body: 'Start at the portfolio picker. Create a portfolio (your program office), then add one or two programs under it. This is the top of the work breakdown — everything else hangs off it.',
      route: '/',
      placement: 'center',
      cta: 'I created a portfolio',
    },
    {
      id: 'resources',
      title: 'Staff your organization',
      body: 'Open Personnel and add a handful of employees with roles and labor rates (e.g. a Program Manager, two Systems Engineers, a Cost Analyst). These become the resources you assign to work later.',
      route: '/personnel',
      targetSelector: '[data-tour="personnel-add"]',
      placement: 'bottom',
      cta: 'I added resources',
    },
    {
      id: 'project-wbs',
      title: 'Stand up a project and its WBS',
      body: 'Create a project, then open WBS Hierarchy and build a 2-3 level work breakdown (Scheduling tab). Use Generate WBS to draft one from a description, or add nodes by hand. A clean WBS is what every downstream calculation rolls up to.',
      route: '/hierarchy',
      targetSelector: '[data-tour="wbs-generate"]',
      placement: 'bottom',
      cta: 'My WBS is built',
    },
    {
      id: 'schedule',
      title: 'Build the schedule',
      body: 'Open the Gantt and use Add Task to place activities under your WBS work packages with durations and dependencies. This is the IMS — the time dimension of the plan.',
      route: '/gantt',
      targetSelector: '[data-tour="schedule-add"]',
      placement: 'bottom',
      cta: 'My schedule is laid out',
    },
    {
      id: 'baseline',
      title: 'Stamp a baseline (PMB)',
      body: 'Open Baseline Management and use New Baseline to freeze your cost + schedule into a Performance Measurement Baseline. Everything you measure variance against is relative to this snapshot.',
      route: '/finance',
      targetSelector: '[data-tour="baseline-new"]',
      placement: 'bottom',
      cta: 'Baseline stamped',
    },
    {
      id: 'risks',
      title: 'Capture risks',
      body: 'Open the Risk Register and use New Risk to add a few with probability, impact, and a disposition (mitigate / accept / transfer). Real programs live and die by their risk posture.',
      route: '/risks',
      targetSelector: '[data-tour="risk-add"]',
      placement: 'bottom',
      cta: 'Risks captured',
    },
    {
      id: 'contract',
      title: 'Open a contract and CLIN structure',
      body: 'Go to Contracts and create a contract with a couple of CLINs (e.g. a CPFF development CLIN and an FFP production CLIN). This ties the work to how you get paid.',
      route: '/contracts',
      placement: 'center',
      cta: 'Contract + CLINs created',
    },
    {
      id: 'timesheet',
      title: 'Log hours against work packages',
      body: 'Open Timesheet and book hours from your resources against the WBS work packages. Actual hours are what turns a plan into earned-value data.',
      route: '/timesheet',
      placement: 'center',
      cta: 'Hours logged',
    },
    {
      id: 'evm',
      title: 'Run an EVM update',
      body: 'Open the EVM Workbench and collect progress on your work packages. Watch CPI and SPI populate from the actuals + baseline you just built.',
      route: '/evm',
      placement: 'center',
      cta: 'I see CPI / SPI',
    },
    {
      id: 'bcr-and-report',
      title: 'File a change and generate a report',
      body: 'File a Baseline Change Request from Baseline Management, route it for approval, then open Reports and generate a status report / IPMDAR narrative. You have now walked the full PMO loop end to end.',
      route: '/reports',
      placement: 'center',
      cta: 'Finish walkthrough',
    },
  ],
}

/**
 * Second scenario (ProjectTasks #109 Phase 4). Same 10 app surfaces and the
 * same domain-agnostic data-tour anchors as the A&D ramp-up, re-framed for a
 * commercial construction firm. Proves the scenario schema generalizes across
 * domains without any framework change — only copy + the same routes/anchors.
 */
export const CONSTRUCTION_PMO: WalkthroughScenario = {
  id: 'construction-pmo',
  name: 'Commercial Construction PMO',
  description: 'Stand up a commercial-construction portfolio: division, field + office staff, a GMP project, and the owner change-order / progress loop.',
  estMinutes: 25,
  steps: [
    {
      id: 'org-structure',
      title: 'Create your firm structure',
      body: 'Start at the portfolio picker. Create a portfolio (your construction division), then add a program or two under it (e.g. Healthcare, Commercial, Civil). Everything else hangs off this.',
      route: '/',
      placement: 'center',
      cta: 'I created a portfolio',
    },
    {
      id: 'resources',
      title: 'Staff the project team',
      body: 'Open Personnel and add field + office staff with labor rates: a Project Executive, two Superintendents, a Project Engineer, and a Cost Estimator. These are the resources you book hours against later.',
      route: '/personnel',
      targetSelector: '[data-tour="personnel-add"]',
      placement: 'bottom',
      cta: 'I added staff',
    },
    {
      id: 'project-wbs',
      title: 'Create a project and its WBS',
      body: 'Create a project (e.g. a 6-story medical office building), then open WBS Hierarchy and build the breakdown by CSI division (Sitework, Concrete, Structural Steel, MEP, Finishes). Use Generate WBS to draft it or add nodes by hand.',
      route: '/hierarchy',
      targetSelector: '[data-tour="wbs-generate"]',
      placement: 'bottom',
      cta: 'My WBS is built',
    },
    {
      id: 'schedule',
      title: 'Build the construction schedule',
      body: 'Open the Gantt and use Add Task to sequence the build: mobilization, foundations, structure, envelope, MEP rough-in, finishes, closeout, with dependencies. This is your CPM schedule.',
      route: '/gantt',
      targetSelector: '[data-tour="schedule-add"]',
      placement: 'bottom',
      cta: 'My schedule is laid out',
    },
    {
      id: 'baseline',
      title: 'Stamp the GMP baseline',
      body: 'Open Baseline Management and use New Baseline to freeze your GMP cost + milestone schedule. Owner change orders and progress are all measured against this snapshot.',
      route: '/finance',
      targetSelector: '[data-tour="baseline-new"]',
      placement: 'bottom',
      cta: 'Baseline stamped',
    },
    {
      id: 'risks',
      title: 'Capture construction risks',
      body: 'Open the Risk Register and use New Risk to add the usual suspects: weather delays, permit timing, long-lead structural steel, subcontractor default, each with probability, impact, and a mitigation.',
      route: '/risks',
      targetSelector: '[data-tour="risk-add"]',
      placement: 'bottom',
      cta: 'Risks captured',
    },
    {
      id: 'contract',
      title: 'Set up the prime contract',
      body: 'Go to Contracts and create the owner prime (e.g. a GMP agreement) with key line items, plus a couple of major subcontracts as CLIN-style lines. This ties the work to how money flows.',
      route: '/contracts',
      placement: 'center',
      cta: 'Contract + subs created',
    },
    {
      id: 'timesheet',
      title: 'Log field hours against cost codes',
      body: 'Open Timesheet and book crew hours from your staff against the WBS work packages / cost codes. Labor productivity is the leading indicator on any build.',
      route: '/timesheet',
      placement: 'center',
      cta: 'Hours logged',
    },
    {
      id: 'evm',
      title: 'Run a progress update',
      body: 'Open the EVM Workbench and collect progress on your work packages. Watch CPI and SPI populate against the GMP baseline — your cost and schedule performance to date.',
      route: '/evm',
      placement: 'center',
      cta: 'I see CPI / SPI',
    },
    {
      id: 'bcr-and-report',
      title: 'File a change order and report out',
      body: 'File an owner change order as a Baseline Change Request from Baseline Management, route it for approval, then open Reports and generate an owner progress report. You have now walked the full construction PMO loop.',
      route: '/reports',
      placement: 'center',
      cta: 'Finish walkthrough',
    },
  ],
}

// ─── Guided journeys (help-excellence H4, 2026-07-19) ───────────────────────
// Cross-page walks over EXISTING data — "how does a whole flow work", spanning
// pages and (where the flow demands it) an explicit persona hand-off step. The
// user switches identity themselves via the banner's persona/Switch-user
// control; a journey never swaps the session silently. Persona names are kept
// generic because casts differ per demo company. Launched from the Help
// Center; hidden from the sandbox simulation launcher (kind: 'journey').
//
// Journeys double as TEST PLANS (Help Center testing plans, 2026-07-21):
// every step carries an `example` (a concrete action to try) and an `expect`
// (the observable result that proves it worked). The overlay records
// It worked / Didn't match per step, and the Help Center card rolls the
// checks up — so walking a journey after a change is a followable
// regression pass, not just a tour.

export const JOURNEY_TIMESHEET_APPROVAL: WalkthroughScenario = {
  id: 'journey-timesheet-approval',
  name: 'A timesheet week, end to end',
  description: 'Follow one week from the worker entering hours, through sign-and-submit, to the manager deciding it, and back to the worker being notified.',
  estMinutes: 7,
  kind: 'journey',
  steps: [
    {
      id: 'become-worker',
      title: 'Become a worker',
      body: 'Use the persona control in the colored banner at the top (Switch user / Acting as) to pick a WORKER persona, e.g. a salaried staff member of the current demo company. The journey follows their week.',
      route: '/timesheet',
      placement: 'center',
      cta: "I'm the worker",
      example: 'Open the banner\'s Switch user control and pick a plain worker (not a manager or admin).',
      expect: 'The banner shows that worker as the acting persona, and the timesheet dashboard cards re-scope to their week and requests.',
    },
    {
      id: 'enter-hours',
      verifyComplete: anyCellFilled(),
      title: 'Look at the week grid',
      body: 'This is My Timesheet: one row per charge line, one column per day, with the expected hours under each date. Click any cell and type to add or adjust hours; note the week-progress ring targets the week net of holidays and approved PTO.',
      route: '/timesheet/my',
      placement: 'center',
      cta: 'I see the grid',
      example: 'If the grid is empty, click "Choose Charge Line" and pick any code first. Then click a weekday cell, type 8, and press Enter (Tab hops to the next day).',
      expect: 'The cell commits as 8.0, the day-column total updates, and the week-progress ring ticks up toward the weekly target.',
    },
    {
      id: 'submit-week',
      // Matches the PERSISTENT post-submit state (the badge/timeline say
      // "awaiting approval"), not just the transient toast — plus the policy
      // gate's rule text, since being blocked-with-reason is also the feature
      // working. Caught live: submit succeeded but the toast was gone before
      // the poller looked.
      verifyComplete: textVisible(/Submitted to|awaiting approval|under its target hours/i),
      title: 'Sign and submit the week',
      body: 'Click "Submit for Approval". The name you type on the attestation certifies every charged day and sends the week to the manager in one step; if a policy is violated (e.g. under the weekly target), the gate explains why before anything sends.',
      route: '/timesheet/my',
      placement: 'center',
      cta: 'Submitted (or saw the gate)',
      example: 'Click Submit for Approval and type the worker\'s name in the attestation box to sign.',
      expect: 'The week flips to Submitted and locks for editing, or the policy gate names the exact rule that blocked it (e.g. under the weekly target).',
    },
    {
      id: 'become-manager',
      verifyComplete: personaChanged(),
      title: 'Switch to the manager',
      body: "Now change hats: use the banner's persona control again and pick the worker's MANAGER (the Manager/Lead persona of this company). Approvals route to them.",
      placement: 'center',
      cta: "I'm the manager",
      example: 'Open the banner\'s persona control and pick the Manager/Lead persona.',
      expect: "The banner shows the manager persona, and the timesheet dashboard's approvals strip shows at least one item waiting on them.",
    },
    {
      id: 'review-queue',
      title: 'Open the Approvals Queue',
      body: "The pending week is here with its clean/not-clean signals and a per-charge-code breakdown. Expand it. This queue also hosts cost-transfer and shift-swap decisions — every approval kind a manager owns is one click from the dashboard's approvals strip.",
      route: '/timesheet/approvals',
      placement: 'center',
      cta: 'I found the week',
      example: "Find the week you just submitted and expand its row to open the per-charge-code breakdown.",
      expect: 'The submitted week is listed as pending, and the expanded breakdown shows the same hours the worker entered, with clean/not-clean signals.',
    },
    {
      id: 'decide',
      title: 'Approve it (or return it)',
      body: 'Approve the week, or reject it with a written reason. Either way the decision is recorded, the CATS status advances on approval, and a notification targeted at the worker fires the moment you act.',
      route: '/timesheet/approvals',
      placement: 'center',
      cta: 'Decided',
      example: 'Click Approve on the week (or Reject and type a reason like "Missing Friday hours").',
      expect: 'The week leaves the pending queue, the decision is recorded with your name and timestamp, and a notification for the worker fires immediately.',
    },
    {
      id: 'back-to-worker',
      verifyComplete: personaChanged(),
      title: 'Switch back to the worker',
      body: 'One more hat-swap: return to the worker persona via the banner control.',
      placement: 'center',
      cta: "I'm the worker again",
      example: 'Use the banner control to switch back to the worker from step 1.',
      expect: 'The banner shows the worker again, and the bell in the top bar carries a new unread badge for the decision.',
    },
    {
      id: 'see-outcome',
      title: 'See the decision arrive',
      body: 'The bell in the top bar shows the decision notice (rejections arrive with the reason attached), and the My Requests card on this dashboard shows the same outcome. A returned week reopens in My Timesheet for revise-and-resubmit. That is the full loop.',
      route: '/timesheet',
      placement: 'center',
      cta: 'Finish journey',
      example: 'Open the bell and click the decision notice, then glance at the My Requests card on this dashboard.',
      expect: 'The notice shows the decision (a rejection includes the written reason), My Requests shows the same outcome, and a returned week is editable again in My Timesheet.',
    },
  ],
}

export const JOURNEY_PTO_REQUEST: WalkthroughScenario = {
  id: 'journey-pto-request',
  name: 'Time off, request to decision',
  description: 'Follow a PTO request from the worker checking their balance, through manager approval, to the decision landing back with the worker.',
  estMinutes: 5,
  kind: 'journey',
  steps: [
    {
      id: 'become-worker',
      title: 'Become a worker',
      body: 'Use the persona control in the banner to pick a WORKER persona of the current demo company.',
      route: '/timesheet',
      placement: 'center',
      cta: "I'm the worker",
      example: "Open the banner's Switch user control and pick a plain worker persona.",
      expect: 'The banner shows that worker as the acting persona.',
    },
    {
      id: 'check-balance',
      title: 'Check the PTO balance',
      body: 'This page shows the ledger-backed balance per time-off type (accruals, uses, refunds). Click "Request time off", pick a type and a date range, and submit it for approval.',
      route: '/timesheet/pto',
      placement: 'center',
      cta: 'Request submitted',
      example: 'Click "Request time off", pick a type (e.g. Vacation) and two days next week, and submit.',
      expect: 'The new request appears as Pending with the dates and hours you picked, and the balance card for that type is unchanged until approval.',
    },
    {
      id: 'become-manager',
      verifyComplete: personaChanged(),
      title: 'Switch to the manager',
      body: "Change hats via the banner's persona control: pick the Manager/Lead persona. Time-off requests route to them.",
      placement: 'center',
      cta: "I'm the manager",
      example: "Use the banner's persona control to pick the Manager/Lead persona.",
      expect: 'The banner shows the manager persona.',
    },
    {
      id: 'decide-pto',
      title: 'Decide the request',
      body: 'The submitted request is in this inbox. Approve it (posts the ledger use and locks the time off in) or reject it with a note. Approval also makes those days net the weekly timesheet target.',
      route: '/timesheet/pto-requests',
      placement: 'center',
      cta: 'Decided',
      example: 'Open the pending request you just submitted and click Approve (or Reject with a note).',
      expect: 'The request flips to its decided state, and on approval the ledger posts the use for those days.',
    },
    {
      id: 'see-outcome',
      title: 'Back to the worker: see the result',
      body: 'Switch back to the worker persona. The bell carries the decision with dates and hours, the balance reflects the use, and the approved days now show in the schedule banner on My Timesheet (the weekly target drops accordingly).',
      route: '/timesheet/pto',
      placement: 'center',
      cta: 'Finish journey',
      example: 'Switch back to the worker, open the bell, then revisit this balance page and My Timesheet.',
      expect: 'The decision notice shows dates and hours, the balance reflects the posted use, and the approved days appear in the My Timesheet schedule banner with a lower weekly target.',
    },
  ],
}

export const JOURNEY_REQUEST_TO_PROJECT: WalkthroughScenario = {
  id: 'journey-request-to-project',
  name: 'From request to project',
  description: 'Follow the PPM intake pipeline: a project request scored and approved, converted to a real project, and visible on the portfolio dashboard.',
  estMinutes: 5,
  kind: 'journey',
  steps: [
    {
      id: 'open-pipeline',
      title: 'Open the intake pipeline',
      body: 'Project Requests is where pursuits, IRADs, and sustainment asks enter. Each row in the highlighted list shows its type, priority, budget, prioritization score, and review status. Open one of the pending requests (or create a new one with New Request).',
      route: '/project-requests',
      targetSelector: '[data-tour="project-requests-list"]',
      placement: 'center',
      cta: 'I opened a request',
      example: 'Click any request row in the list (e.g. one that is Under Review) to open its detail.',
      expect: 'The request detail opens with its prioritization score, budget, sponsor, and requested funding.',
    },
    {
      id: 'score-request',
      title: 'Review the score',
      // No `route`: stay on the request detail the user opened in step 1 (routing
      // back to /project-requests would yank them off the detail to the list).
      body: 'On the request detail you just opened, review what the reviewer weighs before deciding: its Prioritization Score, budget, sponsor, priority alignments, and Business Justification. This is the case for (or against) funding the request. (Not on a request? Open one from the list first.)',
      targetSelector: '[data-tour="project-request-detail-kpis"]',
      placement: 'top',
      cta: 'Reviewed',
      example: 'Read the Prioritization Score card (e.g. 45) and the Business Justification below it on the open request.',
      expect: 'The request detail shows its Prioritization Score, budget, and funding breakdown so you can judge whether to approve it.',
      helpQuery: 'project request detail',
      troubleshoot: [
        'You need to be ON a single request (breadcrumb shows its code, e.g. Preq_…), not the Project Requests list. Click a request row to open it.',
        'The Prioritization Score + budget are the KPI cards near the top of the request detail; the Business Justification is the section below them.',
      ],
    },
    {
      id: 'approve-convert',
      title: 'Approve and convert',
      // No `route`: the approval + workflow controls live on the request DETAIL
      // header, so keep the user there instead of routing to the list.
      body: 'The controls sit in the top-right of the request detail: Run workflow launches the governed approval, and Approve marks it approved. Once a request is Approved a "Convert to Project" button appears there: click it and it creates a real Draft project from the request and jumps you to it.',
      targetSelector: '[data-tour="project-request-detail-header"]',
      placement: 'bottom',
      cta: 'Converted (or saw how it would)',
      example: 'Click Approve on the request, then click the "Convert to Project" button that appears — it creates the project and opens it.',
      expect: 'Approve flips the request to Approved; Convert to Project creates a Draft project and navigates to its detail page.',
      helpQuery: 'project request detail',
      troubleshoot: [
        'The Run workflow / Approve / Reject buttons are in the TOP-RIGHT of the request detail header — if you only see a table of requests, you are on the list; open a request first.',
        '"Convert to Project" only appears AFTER the request is Approved. Approve it first, then the button shows in the same header.',
        'Acting as a manager/admin? Approval controls are role-gated, so switch persona if they are missing.',
      ],
    },
    {
      id: 'find-project',
      title: 'Find the project',
      body: 'Active Projects (highlighted) is where an approved request lands once it becomes a project: every project with its health, budget, SPI/CPI, and PM. New projects start in Draft/Startup, where the 22-step startup checklist takes over.',
      route: '/projects',
      targetSelector: '[data-tour="project-list-table"]',
      placement: 'center',
      cta: 'I see the list',
      example: 'Find the project you just converted (it opened on Convert). Sort Active Projects by status to see it in Draft, the stage a freshly converted request enters.',
      expect: 'The project you converted is in the Active Projects list, starting in Draft, with health, budget, and SPI/CPI columns.',
      helpQuery: 'projects list',
      troubleshoot: [
        'Converting a request creates a real Draft project and jumps you to it; it now also shows in this Active Projects list. Sort by status or search its name to find it.',
      ],
    },
    {
      id: 'portfolio-rollup',
      title: 'See it roll up',
      body: 'The portfolio dashboard counts every project: the highlighted KPIs, plus the budget chart and health pie, roll up the whole active portfolio. Intake to execution, one pipeline. That is the journey.',
      route: '/dashboard',
      targetSelector: '[data-tour="dash-kpis"]',
      placement: 'center',
      cta: 'Finish journey',
      example: 'Read the project-count KPI and the health pie, then compare them against the Active Projects list you just left.',
      expect: 'The dashboard KPIs, budget chart, and health pie reconcile with the Active Projects list.',
      helpQuery: 'portfolio dashboard',
    },
  ],
}

export const WALKTHROUGH_SCENARIOS: WalkthroughScenario[] = [
  AD_PMO_RAMPUP,
  CONSTRUCTION_PMO,
  JOURNEY_TIMESHEET_APPROVAL,
  JOURNEY_PTO_REQUEST,
  JOURNEY_REQUEST_TO_PROJECT,
]

/** The cross-page guided journeys (Help Center's "Guided journeys" section). */
export function listJourneys(): WalkthroughScenario[] {
  return WALKTHROUGH_SCENARIOS.filter((s) => s.kind === 'journey')
}

export function getScenario(id: string): WalkthroughScenario | undefined {
  return WALKTHROUGH_SCENARIOS.find((s) => s.id === id)
}
